import React, { useId } from 'react';
import { Flex, Form } from 'antd';
import { CustomInput, CustomParagraph } from '@/components';

export const pickupInputProps = {
  height: '36px',
  width: '100%',
  border: '0px',
  bgColor: 'var(--light-gray)',
  textColor: 'var(--text-primary)',
  borderRadius: '10px',
} as const;

interface PickupFieldProps {
  htmlFor: string;
  label: string;
  grow?: number;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

export const PickupField: React.FC<PickupFieldProps> = ({
  htmlFor,
  label,
  grow = 1,
  error,
  required,
  children,
}) => (
  <Form.Item
    label={label}
    htmlFor={htmlFor}
    required={required}
    validateStatus={error ? 'error' : undefined}
    help={error}
    style={{ flex: grow, minWidth: 120, marginBottom: 0 }}
  >
    {children}
  </Form.Item>
);

interface PickupTextInputProps {
  id: string;
  value: string;
  onChange: (next: string) => void;
  maxLength?: number;
  placeholder?: string;
}

export const PickupTextInput: React.FC<PickupTextInputProps> = ({
  id,
  value,
  onChange,
  ...extra
}) => (
  <CustomInput
    id={id}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    {...pickupInputProps}
    {...extra}
  />
);

export const PickupSectionTitle: React.FC<{
  text: string;
  heading?: boolean;
}> = ({ text, heading = false }) => (
  <CustomParagraph
    text={text}
    fontWeight={heading ? '700' : '600'}
    fontSize={heading ? 18 : undefined}
    marginTop='4px'
    marginBottom='0'
  />
);

interface PickupReadOnlyFieldProps {
  label: string;
  value?: React.ReactNode;
  /** Icon shown at the right edge, mirroring the editable control it stands in for. */
  trailing?: React.ReactNode;
}

/** Non-editable counterpart of PickupField + input: white background per the platform standard. */
export const PickupReadOnlyField: React.FC<PickupReadOnlyFieldProps> = ({
  label,
  value,
  trailing,
}) => {
  const labelId = useId();
  return (
    <Flex vertical gap={4} style={{ flex: 1, minWidth: 120 }}>
      <span
        id={labelId}
        style={{ fontSize: 14, fontWeight: 600, lineHeight: '16px' }}
      >
        {label}
      </span>
      <Flex
        role='textbox'
        aria-readonly='true'
        aria-labelledby={labelId}
        align='center'
        justify='space-between'
        gap={8}
        style={{
          minHeight: 36,
          padding: '8px 16px',
          background: 'var(--white, #fff)',
          border: '2px solid var(--border-color, #e2e2e2)',
          borderRadius: 8,
          fontSize: 14,
          lineHeight: '20px',
          color: 'var(--text-primary)',
          wordBreak: 'break-word',
        }}
      >
        <span>{value || value === 0 ? value : '—'}</span>
        {trailing}
      </Flex>
    </Flex>
  );
};
